'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/context/LanguageContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { label: t.header.solutions, id: 'solucions' },
    { label: t.header.whyWeb, id: 'per-que-web' },
    { label: t.header.roi, id: 'calculadora-roi' },
    { label: t.header.projects, id: 'projectes' },
    { label: t.header.pricing, id: 'preus' },
    { label: t.header.contact, id: 'contacte' },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'py-3 bg-[#050507]/95 backdrop-blur-xl border-b border-gold/15 shadow-xl shadow-black/50'
          : 'py-5 bg-gradient-to-b from-[#050507]/80 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 group">
          <AppLogo size={36} />
          <div className="flex flex-col">
            <span className="text-xl font-black tracking-tight text-white uppercase group-hover:text-gold transition-colors">
              Onix <span className="text-gold">Digital</span>
            </span>
          </div>
        </Link>

        {/* Nav links - desktop */}
        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-[11px] font-bold uppercase tracking-widest text-slate-300 hover:text-gold transition-colors duration-200"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Right side tools: WhatsApp CTA + Lang + Hamburger */}
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Lang switcher */}
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
            <button
              onClick={() => setLanguage('ca')}
              className={`px-1.5 py-0.5 rounded transition-colors ${
                language === 'ca' ? 'text-gold bg-gold/10 font-black' : 'hover:text-white'
              }`}
            >
              CA
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={() => setLanguage('es')}
              className={`px-1.5 py-0.5 rounded transition-colors ${
                language === 'es' ? 'text-gold bg-gold/10 font-black' : 'hover:text-white'
              }`}
            >
              ES
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={() => setLanguage('en')}
              className={`px-1.5 py-0.5 rounded transition-colors ${
                language === 'en' ? 'text-gold bg-gold/10 font-black' : 'hover:text-white'
              }`}
            >
              EN
            </button>
          </div>

          {/* Main CTA */}
          <button
            onClick={() => scrollTo('contacte')}
            className="px-4 sm:px-5 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-wider text-black bg-gradient-to-r from-gold via-gold-light to-gold-dark hover:brightness-110 shadow-lg shadow-gold/20 transition-all duration-200"
          >
            {t.header.cta}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menú mòbil"
            className="lg:hidden p-2 text-slate-300 hover:text-white focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0a0a0f] border-b border-gold/15 px-6 py-6 space-y-4 animate-in slide-in-from-top">
          <nav className="flex flex-col gap-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-left text-sm font-bold uppercase tracking-wider text-slate-200 hover:text-gold py-2 border-b border-white/5"
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="pt-2">
            <a
              href="https://wa.me/34694919757?text=Hola%2C%20m%27agradaria%20informaci%C3%B3%20sobre%20les%20solucions%20digitals%20d%27Onix%20Digital"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-bold"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
